/** Parsed "Name (123)" segment from a Sendbird distinct-channel title. */
export type ChannelPeerSegment = {
  name: string;
  userId: string;
};

/** Parse Sendbird distinct-channel names like "Jane (1) - Mila J (23)". */
export function parseChannelPeerSegments(
  channelName: string | undefined
): ChannelPeerSegment[] {
  if (!channelName?.trim()) {
    return [];
  }

  return channelName
    .split(" - ")
    .map((part) => {
      const match = part.trim().match(/^(.+?)\s*\((\d+)\)\s*$/);
      if (!match) {
        return null;
      }
      return { name: match[1].trim(), userId: match[2] };
    })
    .filter((segment): segment is ChannelPeerSegment => segment !== null);
}

export function parsePeerUserIdFromChannelName(
  channelName: string | undefined,
  currentUserId: string
): string | null {
  const segments = parseChannelPeerSegments(channelName);
  if (segments.length === 0) {
    return null;
  }

  const others = segments.filter((segment) => segment.userId !== currentUserId);
  if (others.length === 0) {
    return null;
  }
  if (others.length === 1) {
    return others[0].userId;
  }

  const currentUserInName = segments.some(
    (segment) => segment.userId === currentUserId
  );

  // Stale titles can embed ids that no longer match either participant
  // (e.g. "Sarah Johnson (1) - Mila Jablonski (12)" while Sarah is id 23).
  // Channel names are "creator - invitee", so prefer the last segment.
  if (!currentUserInName) {
    return segments[segments.length - 1].userId;
  }

  return others[0].userId;
}

export function parsePeerDisplayNameFromChannelName(
  channelName: string | undefined,
  currentUserId: string
): string | null {
  const peerUserId = parsePeerUserIdFromChannelName(channelName, currentUserId);
  if (!peerUserId) {
    return null;
  }

  return (
    parseChannelPeerSegments(channelName).find(
      (segment) => segment.userId === peerUserId
    )?.name ?? null
  );
}
